import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { SpecialStatusStore } from '@/stores/SpecialStatusStore';
import { orderByField } from '@/utils/orderBy';
import type { ISpecialStatus } from '~/api/ISpecialStatus';
import type { ISpecialStatusRespMsg } from '~/api/messages/ISpecialStatusRespMsg';
import type { ISpecialStatusUpdateRespMsg } from '~/api/messages/ISpecialStatusUpdateRespMsg';

@SignalrListener
@service(SpecialStatusManager)
export class SpecialStatusManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(SpecialStatusStore) protected specialStatusStore: SpecialStatusStore;

  find(id?: string | number) {
    const specialStatusId = typeof id === 'string' ? Number.parseInt(id, 10) : id;
    return this.specialStatusStore.find(specialStatusId);
  }

  get all() {
    const statuses = this.specialStatusStore.all()
      .filter((status) => status.status !== STATUS.DISABLED);
    return orderByField(statuses, this.specialStatusStore.valueKey);
  }

  @SignalrMessage('SpecialStatusRespMsg')
  protected onSpecialStatusRespMsg(response: ISpecialStatusRespMsg) {
    this.update(response.specialStatuses);
  }

  @SignalrMessage('SpecialStatusUpdateRespMsg')
  protected onSpecialStatusUpdateRespMsg(response: ISpecialStatusUpdateRespMsg) {
    this.update([response.specialStatus]);
  }

  update(specialStatuses: ISpecialStatus[]) {
    this.specialStatusStore.batchUpdate(specialStatuses);
  }
}
